import PropTypes from 'prop-types';
import React from 'react';
import { Breadcrumb, BreadcrumbItem } from 'reactstrap';

/**
 * Skeleton table rows for admin list tables. Rendered inside the full
 * `AdminTablePlaceholder`, and on their own inside a loaded table's `<tbody>`
 * to stand in for the page that failed to load during a pagination error.
 * @param {object} props - Component props.
 * @param {number} props.columnCount - Number of cells per row.
 * @param {number} props.rowCount - Number of skeleton rows.
 * @returns {React.ReactElement} Skeleton `<tr>` elements.
 */
export function AdminTablePlaceholderRows({ columnCount = 5, rowCount = 8 }) {
  return [...Array(rowCount)].map((_, rowIndex) => (
    <tr aria-hidden="true" data-testid="admin-table-placeholder-row" key={`placeholder-row-${rowIndex + 1}`}>
      {[...Array(columnCount)].map((__, columnIndex) => (
        <td key={`placeholder-cell-${columnIndex + 1}`}>
          <span className="placeholder placeholder-glow bg-darken placeholder-sm col-10" />
        </td>
      ))}
    </tr>
  ));
}

AdminTablePlaceholderRows.propTypes = {
  columnCount: PropTypes.number,
  rowCount: PropTypes.number,
};

/**
 * Skeleton placeholder for admin list routes (AdminCommunityList, etc.).
 * Mirrors the admin list layout — breadcrumb, page heading, and a table card
 * with a footer button — shown when a field or network error on the route query
 * is caught by PlaceholderErrorBoundary.
 * @param {object} props - Component props.
 * @param {number} props.columnCount - Number of table columns to render.
 * @returns {React.ReactElement} AdminTablePlaceholder component.
 */
function AdminTablePlaceholder({ columnCount = 5 }) {
  const columns = [...Array(columnCount)].map((_, index) => `column-${index + 1}`);

  return (
    <div aria-hidden="true" data-testid="admin-table-placeholder">
      <Breadcrumb>
        <BreadcrumbItem>
          <span className="placeholder placeholder-glow bg-darken" style={{ width: '3rem' }} />
        </BreadcrumbItem>
        <BreadcrumbItem>
          <span className="placeholder placeholder-glow bg-darken" style={{ width: '5.5rem' }} />
        </BreadcrumbItem>
      </Breadcrumb>
      <h1>
        <span className="placeholder placeholder-glow bg-darken" style={{ width: '12rem' }} />
      </h1>

      <div className="mt-4 mb-4 card">
        <div className="table-responsive">
          <table className="table">
            <thead>
              <tr>
                {columns.map((column) => (
                  <th key={column} scope="col">
                    <span className="placeholder placeholder-glow bg-darken col-6" />
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              <AdminTablePlaceholderRows columnCount={columnCount} />
            </tbody>
          </table>
        </div>
        <div className="card-footer">
          <span className="placeholder placeholder-glow bg-darken btn btn-sm" style={{ width: '6rem' }} />
        </div>
      </div>
    </div>
  );
}

AdminTablePlaceholder.propTypes = {
  columnCount: PropTypes.number,
};

export default AdminTablePlaceholder;
